// src/components/automation/CreateRuleModal.tsx
import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Zap, Clock, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { automationService } from '../../services/automation.service';
import {
  AutomationTemplate,
  AutomationRule,
  CreateAutomationRuleDto,
  AutomationTrigger,
  AutomationCondition,
  AutomationAction,
  TRIGGER_EVENTS,
  CONDITION_FIELDS
} from '../../types/automation.types';
import { projectService } from '../../services/project.service';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  template?: AutomationTemplate | null;
  editingRule?: AutomationRule | null;
}

interface Project {
  id: string;
  name: string;
}

export const CreateRuleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  template,
  editingRule
}) => {
  const { userProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<AutomationTrigger>({ type: 'event' });
  const [conditions, setConditions] = useState<Omit<AutomationCondition, 'id'>[]>([]);
  const [conditionLogic, setConditionLogic] = useState<'and' | 'or'>('and');
  const [actions, setActions] = useState<Omit<AutomationAction, 'id'>[]>([]);
  const [projectScope, setProjectScope] = useState<'all' | 'specific'>('all');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  // Template configuration
  const [templateConfig, setTemplateConfig] = useState<Record<string, any>>({});

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description);
      setTrigger(JSON.parse(JSON.stringify(template.trigger)));
      setConditions(JSON.parse(JSON.stringify(template.conditions)));
      setActions(JSON.parse(JSON.stringify(template.actions)));

      // Set default values for configurable fields
      const defaultConfig: Record<string, any> = {};
      template.configurableFields.forEach(field => {
        defaultConfig[field.path] = field.defaultValue;
      });
      setTemplateConfig(defaultConfig);
    } else if (editingRule) {
      setName(editingRule.name);
      setDescription(editingRule.description);
      setTrigger(editingRule.trigger);
      setConditions(editingRule.conditions.map(({ id, ...rest }) => rest));
      setConditionLogic(editingRule.conditionLogic);
      setActions(editingRule.actions.map(({ id, ...rest }) => rest));
      setProjectScope(editingRule.projectScope);
      setSelectedProjectIds(editingRule.projectIds || []);
    } else {
      // Default for custom rule
      setActions([{
        type: 'create_task',
        taskConfig: {
          type: 'general',
          titleTemplate: '',
          descriptionTemplate: '',
          priority: 'medium',
          dueDaysFromTrigger: 1
        },
        assignTo: { type: 'assigned_user' }
      }]);
    }
  }, [template, editingRule]);

  const loadProjects = async () => {
    if (!userProfile) return;
    try {
      const userProjects = await projectService.getUserProjects(userProfile.uid);
      setProjects(userProjects.map(p => ({ id: p.id, name: p.name })));
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const steps = template
    ? [
        { title: 'Configure', subtitle: 'Customize template settings' },
        { title: 'Scope', subtitle: 'Which projects?' },
        { title: 'Review', subtitle: 'Confirm and create' }
      ]
    : [
        { title: 'Basic Info', subtitle: 'Name and description' },
        { title: 'Trigger', subtitle: 'When should this run?' },
        { title: 'Conditions', subtitle: 'Filter criteria (optional)' },
        { title: 'Actions', subtitle: 'What should happen?' },
        { title: 'Scope', subtitle: 'Which projects?' }
      ];

  const canProceed = () => {
    if (template) {
      return true; // Templates have defaults
    }

    switch (step) {
      case 0:
        return name.trim().length > 0;
      case 1:
        return trigger.type === 'event' ? !!trigger.event : !!trigger.schedule?.time;
      case 2:
        return true; // Conditions are optional
      case 3:
        return actions.length > 0 && actions.every(a =>
          a.type === 'create_task' ? a.taskConfig?.titleTemplate : a.notificationConfig?.titleTemplate
        );
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!userProfile) return;

    setSaving(true);
    try {
      if (template) {
        // Create from template with configured values
        await automationService.createRuleFromTemplate(template.id, {
          name,
          description,
          enabled: true,
          projectScope,
          projectIds: projectScope === 'specific' ? selectedProjectIds : undefined,
          ...templateConfig
        }, userProfile.uid);
      } else if (editingRule) {
        // Update existing rule
        await automationService.updateRule(editingRule.id, {
          name,
          description,
          trigger,
          conditions,
          conditionLogic,
          actions,
          projectScope,
          projectIds: projectScope === 'specific' ? selectedProjectIds : undefined
        }, userProfile.uid);
      } else {
        // Create new custom rule
        const ruleData: CreateAutomationRuleDto = {
          name,
          description,
          enabled: true,
          trigger,
          conditions,
          conditionLogic,
          actions,
          projectScope,
          projectIds: projectScope === 'specific' ? selectedProjectIds : undefined
        };
        await automationService.createRule(ruleData, userProfile.uid);
      }

      onClose();
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save automation rule');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const renderTemplateConfigStep = () => {
    if (!template) return null;

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={2}
          />
        </div>

        {template.configurableFields.length > 0 && (
          <div className="border-t pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Customizable Settings</h4>
            <div className="space-y-4">
              {template.configurableFields.map((field) => (
                <div key={field.path}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.description && (
                    <p className="text-xs text-gray-500 mb-1">{field.description}</p>
                  )}

                  {field.type === 'number' && (
                    <input
                      type="number"
                      value={templateConfig[field.path] ?? field.defaultValue}
                      onChange={(e) => setTemplateConfig({
                        ...templateConfig,
                        [field.path]: parseInt(e.target.value)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  )}

                  {field.type === 'time' && (
                    <input
                      type="time"
                      value={templateConfig[field.path] ?? field.defaultValue}
                      onChange={(e) => setTemplateConfig({
                        ...templateConfig,
                        [field.path]: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  )}

                  {field.type === 'select' && field.options && (
                    <select
                      value={templateConfig[field.path] ?? field.defaultValue}
                      onChange={(e) => setTemplateConfig({
                        ...templateConfig,
                        [field.path]: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBasicInfoStep = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g., Day Before Appointment Reminder"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          rows={3}
          placeholder="Describe what this automation does..."
        />
      </div>
    </div>
  );

  const renderTriggerStep = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Trigger Type</label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setTrigger({ type: 'event' })}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              trigger.type === 'event'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Zap className={`h-6 w-6 mb-2 ${trigger.type === 'event' ? 'text-primary-600' : 'text-gray-400'}`} />
            <div className="font-medium">Event-based</div>
            <div className="text-sm text-gray-500">Run when something happens</div>
          </button>
          <button
            type="button"
            onClick={() => setTrigger({ type: 'scheduled', schedule: { type: 'fixed', time: '09:00' } })}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              trigger.type === 'scheduled'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Clock className={`h-6 w-6 mb-2 ${trigger.type === 'scheduled' ? 'text-primary-600' : 'text-gray-400'}`} />
            <div className="font-medium">Scheduled</div>
            <div className="text-sm text-gray-500">Run at specific times</div>
          </button>
        </div>
      </div>

      {trigger.type === 'event' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Event</label>
          <div className="space-y-2">
            {TRIGGER_EVENTS.map((event) => (
              <button
                key={event.value}
                type="button"
                onClick={() => setTrigger({ ...trigger, event: event.value })}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                  trigger.event === event.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">{event.label}</div>
                <div className="text-sm text-gray-500">{event.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {trigger.type === 'scheduled' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Schedule Type</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setTrigger({
                  ...trigger,
                  schedule: { type: 'fixed', time: '09:00' }
                })}
                className={`p-3 rounded-lg border-2 text-left ${
                  trigger.schedule?.type === 'fixed'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Daily</div>
                <div className="text-sm text-gray-500">Run every day at a set time</div>
              </button>
              <button
                type="button"
                onClick={() => setTrigger({
                  ...trigger,
                  schedule: { type: 'relative', relativeTo: 'appointment_date', offsetDays: -1, time: '09:00' }
                })}
                className={`p-3 rounded-lg border-2 text-left ${
                  trigger.schedule?.type === 'relative'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Relative</div>
                <div className="text-sm text-gray-500">Run relative to a date</div>
              </button>
            </div>
          </div>

          {trigger.schedule?.type === 'fixed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
              <input
                type="time"
                value={trigger.schedule?.time || '09:00'}
                onChange={(e) => setTrigger({
                  ...trigger,
                  schedule: { ...trigger.schedule, type: 'fixed', time: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}

          {trigger.schedule?.type === 'relative' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relative To</label>
                <select
                  value={trigger.schedule?.relativeTo || 'appointment_date'}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule: { ...trigger.schedule, type: 'relative', relativeTo: e.target.value as any }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="appointment_date">Appointment Date</option>
                  <option value="lead_created">Lead Created Date</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Offset Days</label>
                <input
                  type="number"
                  value={trigger.schedule?.offsetDays || 0}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule: { ...trigger.schedule, type: 'relative', offsetDays: parseInt(e.target.value) }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Use negative numbers for days before (e.g., -1 for day before)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
                <input
                  type="time"
                  value={trigger.schedule?.time || '09:00'}
                  onChange={(e) => setTrigger({
                    ...trigger,
                    schedule: { ...trigger.schedule, time: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderConditionsStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Add conditions to filter when this rule should run. Leave empty to run for all matching triggers.
      </p>

      {conditions.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600">Match</span>
          <select
            value={conditionLogic}
            onChange={(e) => setConditionLogic(e.target.value as 'and' | 'or')}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="and">ALL conditions</option>
            <option value="or">ANY condition</option>
          </select>
        </div>
      )}

      {conditions.map((condition, index) => (
        <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
          <select
            value={condition.field}
            onChange={(e) => {
              const updated = [...conditions];
              updated[index] = { ...condition, field: e.target.value };
              setConditions(updated);
            }}
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="">Select field...</option>
            {CONDITION_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>

          <select
            value={condition.operator}
            onChange={(e) => {
              const updated = [...conditions];
              updated[index] = { ...condition, operator: e.target.value as any };
              setConditions(updated);
            }}
            className="px-2 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="equals">equals</option>
            <option value="not_equals">not equals</option>
            <option value="greater_than">greater than</option>
            <option value="less_than">less than</option>
          </select>

          <input
            type="text"
            value={condition.value}
            onChange={(e) => {
              const updated = [...conditions];
              updated[index] = { ...condition, value: e.target.value };
              setConditions(updated);
            }}
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
            placeholder="Value"
          />

          <button
            onClick={() => setConditions(conditions.filter((_, i) => i !== index))}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      <button
        onClick={() => setConditions([...conditions, { field: '', operator: 'equals', value: '' }])}
        className="text-sm text-primary-600 hover:text-primary-700"
      >
        + Add condition
      </button>
    </div>
  );

  const renderActionsStep = () => (
    <div className="space-y-4">
      {actions.map((action, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <select
              value={action.type}
              onChange={(e) => {
                const updated = [...actions];
                const newType = e.target.value as 'create_task' | 'send_notification';
                updated[index] = {
                  type: newType,
                  assignTo: action.assignTo,
                  ...(newType === 'create_task' ? {
                    taskConfig: {
                      type: 'general',
                      titleTemplate: '',
                      descriptionTemplate: '',
                      priority: 'medium',
                      dueDaysFromTrigger: 1
                    }
                  } : {
                    notificationConfig: {
                      titleTemplate: '',
                      messageTemplate: '',
                      channels: ['in_app']
                    }
                  })
                };
                setActions(updated);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
            >
              <option value="create_task">Create Task</option>
              <option value="send_notification">Send Notification</option>
            </select>

            {actions.length > 1 && (
              <button
                onClick={() => setActions(actions.filter((_, i) => i !== index))}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {action.type === 'create_task' && action.taskConfig && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Task Title</label>
                <input
                  type="text"
                  value={action.taskConfig.titleTemplate}
                  onChange={(e) => {
                    const updated = [...actions];
                    updated[index] = {
                      ...action,
                      taskConfig: { ...action.taskConfig!, titleTemplate: e.target.value }
                    };
                    setActions(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="e.g., Follow up with {{lead.parentName}}"
                />
                <p className="text-xs text-gray-400 mt-1">Use {'{{lead.parentName}}'}, {'{{appointment.date}}'}, etc.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={action.taskConfig.descriptionTemplate || ''}
                  onChange={(e) => {
                    const updated = [...actions];
                    updated[index] = {
                      ...action,
                      taskConfig: { ...action.taskConfig!, descriptionTemplate: e.target.value }
                    };
                    setActions(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={action.taskConfig.priority}
                    onChange={(e) => {
                      const updated = [...actions];
                      updated[index] = {
                        ...action,
                        taskConfig: { ...action.taskConfig!, priority: e.target.value as any }
                      };
                      setActions(updated);
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due In (Days)</label>
                  <input
                    type="number"
                    value={action.taskConfig.dueDaysFromTrigger}
                    onChange={(e) => {
                      const updated = [...actions];
                      updated[index] = {
                        ...action,
                        taskConfig: { ...action.taskConfig!, dueDaysFromTrigger: parseInt(e.target.value) }
                      };
                      setActions(updated);
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {action.type === 'send_notification' && action.notificationConfig && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={action.notificationConfig.titleTemplate}
                  onChange={(e) => {
                    const updated = [...actions];
                    updated[index] = {
                      ...action,
                      notificationConfig: { ...action.notificationConfig!, titleTemplate: e.target.value }
                    };
                    setActions(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={action.notificationConfig.messageTemplate}
                  onChange={(e) => {
                    const updated = [...actions];
                    updated[index] = {
                      ...action,
                      notificationConfig: { ...action.notificationConfig!, messageTemplate: e.target.value }
                    };
                    setActions(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Channels</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={action.notificationConfig.channels.includes('in_app')}
                      onChange={(e) => {
                        const updated = [...actions];
                        const channels = e.target.checked
                          ? [...action.notificationConfig!.channels, 'in_app']
                          : action.notificationConfig!.channels.filter(c => c !== 'in_app');
                        updated[index] = {
                          ...action,
                          notificationConfig: { ...action.notificationConfig!, channels: channels as any }
                        };
                        setActions(updated);
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">In-App</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={action.notificationConfig.channels.includes('email')}
                      onChange={(e) => {
                        const updated = [...actions];
                        const channels = e.target.checked
                          ? [...action.notificationConfig!.channels, 'email']
                          : action.notificationConfig!.channels.filter(c => c !== 'email');
                        updated[index] = {
                          ...action,
                          notificationConfig: { ...action.notificationConfig!, channels: channels as any }
                        };
                        setActions(updated);
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Email</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t">
            <label className="block text-xs font-medium text-gray-700 mb-1">Assign To</label>
            <select
              value={action.assignTo.type}
              onChange={(e) => {
                const updated = [...actions];
                updated[index] = {
                  ...action,
                  assignTo: { type: e.target.value as any }
                };
                setActions(updated);
              }}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="assigned_user">Assigned User (client_user)</option>
              <option value="captured_by">Field User Who Captured</option>
              <option value="role">By Role</option>
            </select>

            {action.assignTo.type === 'role' && (
              <select
                value={action.assignTo.role as string || ''}
                onChange={(e) => {
                  const updated = [...actions];
                  updated[index] = {
                    ...action,
                    assignTo: { type: 'role', role: e.target.value }
                  };
                  setActions(updated);
                }}
                className="w-full mt-2 px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="">Select role...</option>
                <option value="provider_admin">Provider Admin</option>
                <option value="provider_user">Provider User</option>
                <option value="client_admin">Client Admin</option>
                <option value="client_user">Client User</option>
                <option value="field_user">Field User</option>
              </select>
            )}
          </div>
        </div>
      ))}

      <button
        onClick={() => setActions([...actions, {
          type: 'send_notification',
          notificationConfig: {
            titleTemplate: '',
            messageTemplate: '',
            channels: ['in_app']
          },
          assignTo: { type: 'assigned_user' }
        }])}
        className="text-sm text-primary-600 hover:text-primary-700"
      >
        + Add another action
      </button>
    </div>
  );

  const renderScopeStep = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Project Scope</label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="scope"
              checked={projectScope === 'all'}
              onChange={() => setProjectScope('all')}
              className="text-primary-600"
            />
            <div>
              <div className="font-medium">All Projects</div>
              <div className="text-sm text-gray-500">Run this rule for all projects</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="scope"
              checked={projectScope === 'specific'}
              onChange={() => setProjectScope('specific')}
              className="text-primary-600"
            />
            <div>
              <div className="font-medium">Specific Projects</div>
              <div className="text-sm text-gray-500">Only run for selected projects</div>
            </div>
          </label>
        </div>
      </div>

      {projectScope === 'specific' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Projects</label>
          <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
            {projects.map((project) => (
              <label
                key={project.id}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedProjectIds.includes(project.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProjectIds([...selectedProjectIds, project.id]);
                    } else {
                      setSelectedProjectIds(selectedProjectIds.filter(id => id !== project.id));
                    }
                  }}
                  className="rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm">{project.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">{name}</h4>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Trigger</div>
          <div className="text-sm text-gray-900">
            {trigger.type === 'event' ? trigger.event?.replace('_', ' ') : 'Scheduled'}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Scope</div>
          <div className="text-sm text-gray-900">
            {projectScope === 'all' ? 'All Projects' : `${selectedProjectIds.length} project(s)`}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-xs font-medium text-gray-500 uppercase mb-2">Actions</div>
        <div className="space-y-2">
          {actions.map((action, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <span className="capitalize">{action.type.replace('_', ' ')}</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-600">{action.assignTo.type.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const getCurrentStepContent = () => {
    if (template) {
      switch (step) {
        case 0: return renderTemplateConfigStep();
        case 1: return renderScopeStep();
        case 2: return renderReviewStep();
      }
    } else {
      switch (step) {
        case 0: return renderBasicInfoStep();
        case 1: return renderTriggerStep();
        case 2: return renderConditionsStep();
        case 3: return renderActionsStep();
        case 4: return renderScopeStep();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold">
              {editingRule ? 'Edit Rule' : template ? `Create from: ${template.name}` : 'Create Automation Rule'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Step {step + 1} of {steps.length}: {steps[step].subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 bg-gray-50 border-b flex-shrink-0">
          <div className="flex gap-2">
            {steps.map((s, i) => (
              <div key={i} className="flex-1">
                <div
                  className={`h-2 rounded-full transition-colors ${
                    i <= step ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
                <span className={`text-xs mt-1 block ${
                  i === step ? 'text-primary-600 font-medium' : 'text-gray-400'
                }`}>
                  {s.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {getCurrentStepContent()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between flex-shrink-0">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>

          <div className="space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>

            {step < steps.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingRule ? 'Save Changes' : 'Create Rule'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRuleModal;
